import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { environment } from '@curbnturf/environment';
import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  ICognitoUserSessionData,
  ISignUpResult,
} from 'amazon-cognito-identity-js';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PlatformService } from './platform.service';
import { SafariViewWrapperService } from './safari-view-wrapper.service';
import { webCognitoCallback, webCognitoLogoutCallback } from './web-callback';
import { cognitoPreferencesStorage } from '../../storage/cognito-preferences-storage.util';

const AWS_REGION = environment.awsRegion;
const COGNITO_USER_POOL_ID = environment.cognitoUserPoolId;
const COGNITO_CLIENT_ID = environment.cognitoClientId;
const COGNITO_DOMAIN = environment.cognitoDomain;
const COGNITO_CALLBACK = environment.cognitoCallback;
const COGNITO_CALLBACK_IOS = environment.cognitoCallbackIOS;
const COGNITO_CALLBACK_APP = environment.cognitoCallbackApp;
const COGNITO_LOGOUT_CALLBACK = environment.baseUrl;
const COGNITO_LOGOUT_CALLBACK_IOS = environment.cognitoLogoutCallbackIOS;

const requestUrl = `https://cognito-idp.${AWS_REGION}.amazonaws.com`;

/**
 * Narrow structural slice of @curbnturf/entities' IUser — only the fields a
 * Cognito session actually produces. Every field optional, same as the real
 * IUser, so this is freely assignable wherever AuthService passes a result
 * into an IUser-typed slot.
 */
export interface ICognitoUser {
  authAccessToken?: string;
  authIdToken?: string;
  authRenewToken?: string;
  authExpires?: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  temporaryPassword?: boolean;
}

/**
 * Local fork of @curbnturf/auth's CognitoService — a thin
 * amazon-cognito-identity-js wrapper with no curbnturf business logic and no
 * NgRx dependency, so the only changes from the real class are: Cognito
 * pool/domain/callback constants read directly from cnt-landing's own
 * environment (instead of via @curbnturf/entities' constants.ts re-export of
 * the same values), and IUser replaced with the narrower local ICognitoUser.
 */
@Injectable()
export class CognitoService {
  userPool: CognitoUserPool = new CognitoUserPool({
    UserPoolId: COGNITO_USER_POOL_ID,
    ClientId: COGNITO_CLIENT_ID,
    // Without this, amazon-cognito-identity-js defaults to raw
    // window.localStorage, which a Capacitor WKWebView/Android WebView can
    // opportunistically evict under OS storage pressure — see
    // cognito-preferences-storage.util.ts's header comment.
    Storage: cognitoPreferencesStorage,
  });

  authUser: CognitoUser;

  version: number;

  private readonly httpClient = inject(HttpClient);
  private readonly platformService = inject(PlatformService);
  private readonly safariViewController = inject(SafariViewWrapperService);

  public signUp(email: string, password: string): Promise<ISignUpResult | undefined> {
    return new Promise((resolve, reject) => {
      this.userPool.signUp(email.toLocaleLowerCase(), password, [], [], (err, result) => {
        if (err) {
          return reject(err);
        }

        return resolve(result);
      });
    });
  }

  public authenticateUser(email: string, password: string): Promise<ICognitoUser> {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email.toLocaleLowerCase(),
        Password: password,
      });

      const userData = {
        Username: email.toLocaleLowerCase(),
        Pool: this.userPool,
        Storage: cognitoPreferencesStorage,
      };

      this.authUser = new CognitoUser(userData);
      this.authUser.authenticateUser(authenticationDetails, {
        onSuccess: (result: CognitoUserSession): void => {
          resolve(this.sessionToUser(result));
        },
        onFailure: (err: Error): void => {
          reject(err);
        },
        newPasswordRequired: (userAttributes): void => {
          resolve({ ...userAttributes, temporaryPassword: true });
        },
      });
    });
  }

  public newPassword(password: string): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.authUser) {
        reject('Must be authenticated to update password');
      }

      this.authUser.completeNewPasswordChallenge(
        password,
        {},
        {
          onSuccess: (): void => {
            resolve('Done');
          },
          onFailure: (err: Error): void => {
            reject(err);
          },
        },
      );
    });
  }

  public renew(renewToken: string, idToken: string): Observable<any> {
    const requestPayload = {
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      AuthParameters: {
        REFRESH_TOKEN: renewToken,
      },
      ClientId: COGNITO_CLIENT_ID,
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    });

    return this.httpClient
      .post(requestUrl, requestPayload, {
        headers,
      })
      .pipe(
        map((result: any) => {
          const session = this.sessionFromData({
            ...result.AuthenticationResult,
            RefreshToken: renewToken,
          });

          if (!session || !session.getIdToken() || !session.getIdToken().getJwtToken()) {
            throw new Error('Unable to get session data from authentication service.');
          }

          const idPayload = new CognitoIdToken({
            IdToken: session.getIdToken().getJwtToken(),
          }).decodePayload();

          if (!idPayload || !idPayload['email']) {
            throw new Error('Received invalid session data from authentication service.');
          }

          const userData = {
            Username: idPayload['email'].toLocaleLowerCase(),
            Pool: this.userPool,
            Storage: cognitoPreferencesStorage,
          };

          this.authUser = new CognitoUser(userData);

          this.authUser.setSignInUserSession(session);
          return this.sessionToUser(session);
        }),
      );
  }

  public resendVerification(email: string): Observable<any> {
    const requestPayload = {
      Username: email.toLocaleLowerCase(),
      ClientId: COGNITO_CLIENT_ID,
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.ResendConfirmationCode',
    });

    return this.httpClient.post(requestUrl, requestPayload, {
      headers,
    });
  }

  public resetPassword(email: string): Observable<ICognitoUser> {
    const requestPayload = {
      Username: email.toLocaleLowerCase(),
      ClientId: COGNITO_CLIENT_ID,
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.ForgotPassword',
    });

    return this.httpClient.post(requestUrl, requestPayload, {
      headers,
    });
  }

  public confirmResetPassword(email: string, verificationCode: string, password: string): Observable<ICognitoUser> {
    const requestPayload = {
      Username: email.toLocaleLowerCase(),
      ConfirmationCode: verificationCode,
      Password: password,
      ClientId: COGNITO_CLIENT_ID,
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmForgotPassword',
    });

    return this.httpClient.post(requestUrl, requestPayload, {
      headers,
    });
  }

  public changePassword(password: string, newPassword: string, accessToken: string): Observable<boolean> {
    const requestPayload = {
      AccessToken: accessToken,
      PreviousPassword: password,
      ProposedPassword: newPassword,
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.ChangePassword',
    });

    return this.httpClient
      .post(requestUrl, requestPayload, {
        headers,
      })
      .pipe(map(() => true)); // Assuming true unless a 400 is returned
  }

  public codeToUser(code: string): Observable<ICognitoUser> {
    const redirectURI = Capacitor.isNativePlatform()
      ? this.platformService.isIOS()
        ? COGNITO_CALLBACK_IOS
        : COGNITO_CALLBACK_APP
      : webCognitoCallback(COGNITO_CALLBACK);

    const requestPayload = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('code', code)
      .set('client_id', COGNITO_CLIENT_ID)
      .set('redirect_uri', redirectURI);

    return this.httpClient
      .post(`https://${COGNITO_DOMAIN}/oauth2/token`, requestPayload, {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
      })
      .pipe(
        map((result: any) => {
          const token = {
            IdToken: result.id_token,
            AccessToken: result.access_token,
            RefreshToken: result.refresh_token,
          };

          const session = this.sessionFromData(token);

          const payload = session.getIdToken().decodePayload();

          const userData = {
            Username: payload['email']?.toLocaleLowerCase(),
            Pool: this.userPool,
            Storage: cognitoPreferencesStorage,
          };

          this.authUser = new CognitoUser(userData);

          this.authUser.setSignInUserSession(session);

          return this.sessionToUser(session);
        }),
      );
  }

  public async signOut(token: { IdToken: string; AccessToken: string; RefreshToken: string }) {
    const session = this.sessionFromData(token);
    const payload = session.getIdToken().decodePayload();

    // Always clear the locally persisted Cognito session first, regardless of
    // whether this was a password or federated (social) sign-in — the social
    // branch below only clears Cognito's Hosted UI SSO cookie, not the local
    // session, so skipping this left social users still "signed in" locally.
    const userData = {
      Username: payload['email']?.toLocaleLowerCase(),
      Pool: this.userPool,
      Storage: cognitoPreferencesStorage,
    };
    this.authUser = new CognitoUser(userData);
    this.authUser.signOut();

    if (payload['identities'] && payload['identities'].length > 0) {
      const logoutRedirect = this.buildSocialLogoutLink();
      if (Capacitor.isNativePlatform()) {
        // Check for the availability of the Safari View Controller
        this.safariViewController.isAvailable().then(
          (available: boolean) => {
            if (available) {
              this.safariViewController.launchBrowserUI(logoutRedirect, async (result) => {
                if (result.event === 'loaded') {
                  await this.safariViewController.closeAll();
                }
              });
            } else {
              console.error('Attempted Safari View Controller, but was not available.');
              // Fall back to browser link
              window.location.href = logoutRedirect;
            }
          },
          (e) => {
            console.error('Attempted Safari View Controller, but an error occurred.', e);
          },
        );
      } else {
        window.location.href = logoutRedirect;
      }
    }
  }

  private sessionFromData(result: { IdToken: string; AccessToken: string; RefreshToken: string }): CognitoUserSession {
    const token: ICognitoUserSessionData = {
      IdToken: new CognitoIdToken({ IdToken: result.IdToken }),
      AccessToken: new CognitoAccessToken({ AccessToken: result.AccessToken }),
      RefreshToken: new CognitoRefreshToken({
        RefreshToken: result.RefreshToken,
      }),
    };

    return new CognitoUserSession(token);
  }

  private sessionToUser(result: CognitoUserSession): ICognitoUser {
    const payload = result.getIdToken().decodePayload();

    return {
      authAccessToken: result.getAccessToken().getJwtToken(),
      authIdToken: result.getIdToken().getJwtToken(),
      authRenewToken: result.getRefreshToken().getToken(),
      authExpires: result.getIdToken().getExpiration() * 1000,
      email: payload['email'].toLocaleLowerCase(),
      firstName: payload['given_name'],
      lastName: payload['family_name'],
    };
  }

  private buildSocialLogoutLink() {
    let callback = webCognitoLogoutCallback(COGNITO_LOGOUT_CALLBACK);
    if (Capacitor.isNativePlatform()) {
      if (this.platformService.isIOS()) {
        callback = COGNITO_LOGOUT_CALLBACK_IOS;
      } else {
        callback = COGNITO_LOGOUT_CALLBACK;
      }
    }

    return (
      `https://${COGNITO_DOMAIN}/logout?logout_uri=${callback}` +
      `&client_id=${COGNITO_CLIENT_ID}&redirect_uri=${callback}&response_type=code`
    );
  }
}
